import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Bug, TrendingUp, Shield, Calendar, User } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showError } from '../../utils/notificationHandler';

const ChangelogModal = ({ isOpen, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    if (isOpen) {
      fetchChangelog();
      markAllAsViewed();
    }
  }, [isOpen]);

  const fetchChangelog = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('app_changelog')
        .select(`
          *,
          accounts:created_by (
            full_name
          )
        `)
        .eq('is_published', true)
        .order('published_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Sort entries client-side to ensure latest first (fallback to created_at if published_date is null)
      const sortedData = (data || []).sort((a, b) => {
        const dateA = new Date(a.published_date || a.created_at);
        const dateB = new Date(b.published_date || b.created_at);
        return dateB - dateA; // Descending order (latest first)
      });
      
      setEntries(sortedData);
    } catch (error) {
      console.error('Error fetching changelog:', error);
      showError(error, 'Failed to load changelog');
    } finally {
      setLoading(false);
    }
  };

  const markAllAsViewed = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get all published entries
      const { data: publishedEntries } = await supabase
        .from('app_changelog')
        .select('id')
        .eq('is_published', true);

      if (!publishedEntries || publishedEntries.length === 0) return;

      // Get already viewed entries
      const { data: viewedEntries } = await supabase
        .from('changelog_views')
        .select('changelog_id')
        .eq('user_id', user.id);

      const viewedIds = new Set((viewedEntries || []).map(v => v.changelog_id));
      
      // Insert new view records for unviewed entries
      const unviewedEntries = publishedEntries.filter(entry => !viewedIds.has(entry.id));
      
      if (unviewedEntries.length > 0) {
        const viewRecords = unviewedEntries.map(entry => ({
          user_id: user.id,
          changelog_id: entry.id
        }));

        await supabase
          .from('changelog_views')
          .insert(viewRecords);
      }
    } catch (error) {
      console.error('Error marking changelog as viewed:', error);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'feature':
        return <Sparkles className="h-5 w-5" />;
      case 'bugfix':
        return <Bug className="h-5 w-5" />;
      case 'improvement':
        return <TrendingUp className="h-5 w-5" />;
      case 'security':
        return <Shield className="h-5 w-5" />;
      default:
        return <Sparkles className="h-5 w-5" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'feature':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'bugfix':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'improvement':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'security':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'feature':
        return 'New Feature';
      case 'bugfix':
        return 'Bug Fix';
      case 'improvement':
        return 'Improvement';
      case 'security':
        return 'Security';
      default:
        return category;
    }
  };

  const filteredEntries = selectedCategory === 'all'
    ? entries
    : entries.filter(entry => entry.category === selectedCategory);

  const categories = [
    { value: 'all', label: 'All Updates', count: entries.length },
    { value: 'feature', label: 'Features', count: entries.filter(e => e.category === 'feature').length },
    { value: 'improvement', label: 'Improvements', count: entries.filter(e => e.category === 'improvement').length },
    { value: 'bugfix', label: 'Bug Fixes', count: entries.filter(e => e.category === 'bugfix').length },
    { value: 'security', label: 'Security', count: entries.filter(e => e.category === 'security').length },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
          />

          {/* Modal */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full sm:w-[500px] bg-white shadow-2xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-brand to-brand-hover px-6 py-4 flex items-center justify-between border-b border-gray-200">
              <div className="flex items-center">
                <Sparkles className="h-6 w-6 text-black mr-3" />
                <h2 className="text-xl font-bold text-black">What's New</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-black/10 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-black" />
              </button>
            </div>

            {/* Category Filter */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button
                    key={cat.value}
                    onClick={() => setSelectedCategory(cat.value)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === cat.value
                        ? 'bg-brand text-black'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    {cat.label} {cat.count > 0 && `(${cat.count})`}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                      <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-16 bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </div>
              ) : filteredEntries.length === 0 ? (
                <div className="text-center py-12">
                  <Sparkles className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No updates yet</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Check back later for new features and improvements!
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredEntries.map((entry) => (
                    <motion.div
                      key={entry.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      {/* Entry Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(entry.category)}`}>
                            {getCategoryIcon(entry.category)}
                            <span className="ml-1.5">{getCategoryLabel(entry.category)}</span>
                          </span>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-mono rounded">
                            {entry.version}
                          </span>
                        </div>
                      </div>

                      {/* Entry Content */}
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {entry.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                        {entry.description}
                      </p>

                      {/* Entry Footer */}
                      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {entry.published_date ? (
                            new Date(entry.published_date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })
                          ) : (
                            new Date(entry.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })
                          )}
                        </div>
                        {entry.accounts && (
                          <div className="flex items-center">
                            <User className="h-3 w-3 mr-1" />
                            {entry.accounts.full_name}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-500 text-center">
                Stay updated with the latest features and improvements
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ChangelogModal;
